import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface Pattern {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  hours: string;
  description?: string;
}

const PATTERN_STYLE: Record<string, string> = {
  Early: 'bg-amber-100 text-amber-900',
  Late: 'bg-indigo-100 text-indigo-900',
  'Full Day': 'bg-emerald-100 text-emerald-900',
  'Weekend Early': 'bg-amber-100 text-amber-900',
};

function weekDays(from: Date) {
  const monday = new Date(from);
  const offset = (monday.getDay() + 6) % 7; // Monday = 0
  monday.setDate(monday.getDate() - offset);
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

export default function RotaPanel({ branchId, branchName }: { branchId: string; branchName?: string }) {
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [periods, setPeriods] = useState<any[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [weekStart, setWeekStart] = useState(() => new Date());

  const days = weekDays(weekStart);

  const load = async () => {
    if (!branchId) return;
    try {
      const [pat, per] = await Promise.all([
        apiClient.getShiftPatterns(),
        apiClient.listRotaPeriods(branchId).catch(() => ({ data: { periods: [] } })),
      ]);
      setPatterns(Array.isArray(pat.data) ? pat.data : []);
      const periodList = per.data?.periods || [];
      setPeriods(periodList);

      // Pull assigned shifts from the period covering the shown week, if one exists
      const covering = periodList.find((p: any) => {
        const s = new Date(p.startDate);
        const e = new Date(p.endDate);
        return s <= days[6] && e >= days[0];
      });
      if (covering) {
        const detail = await apiClient.getRotaPeriod(covering.id).catch(() => null);
        setShifts(detail?.data?.shifts || []);
      } else {
        setShifts([]);
      }
      setErr('');
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not load rota');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branchId, weekStart.toDateString()]);

  if (loading) return <div className="text-gray-500 py-8">Loading rota…</div>;

  // Group assigned shifts by staff, then by day
  const byStaff = new Map<string, { name: string; days: Record<string, any> }>();
  for (const sh of shifts) {
    const name = sh.staffName || sh.staff?.name || 'Staff';
    const key = sh.staffId || name;
    if (!byStaff.has(key)) byStaff.set(key, { name, days: {} });
    const dayKey = new Date(sh.shiftDate).toDateString();
    byStaff.get(key)!.days[dayKey] = sh;
  }
  const staffRows = [...byStaff.values()].sort((a, b) => a.name.localeCompare(b.name));

  const shiftWeek = (delta: number) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
  };

  const pendingCount = shifts.filter((s) => s.status === 'REQUESTED' || s.status === 'PENDING').length;

  return (
    <div className="space-y-6">
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Shift patterns</h3>
          <p className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">{patterns.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Shifts this week</h3>
          <p className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">{shifts.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Awaiting approval</h3>
          <p className={`mt-1 text-3xl font-bold tabular-nums ${pendingCount ? 'text-amber-600' : 'text-gray-900'}`}>
            {pendingCount}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Rota periods</h3>
          <p className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">{periods.length}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Week of {days[0].toLocaleDateString('en-ZA', { day: 'numeric', month: 'long' })}
            </h3>
            {branchName && <p className="text-sm text-gray-500">{branchName}</p>}
          </div>
          <div className="flex gap-2">
            <button onClick={() => shiftWeek(-1)} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-100 hover:bg-gray-200">
              ← Previous
            </button>
            <button onClick={() => setWeekStart(new Date())} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-100 hover:bg-gray-200">
              This week
            </button>
            <button onClick={() => shiftWeek(1)} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-100 hover:bg-gray-200">
              Next →
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-2.5 px-6 text-xs font-semibold uppercase tracking-wide text-gray-600">Staff</th>
                {days.map((d) => (
                  <th key={d.toDateString()} className="py-2.5 px-2 text-xs font-semibold uppercase tracking-wide text-gray-600 text-center">
                    {d.toLocaleDateString('en-ZA', { weekday: 'short' })}
                    <span className="block font-normal text-gray-400 tabular-nums">{d.getDate()}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staffRows.map((row) => (
                <tr key={row.name} className="border-b last:border-0">
                  <td className="py-2.5 px-6 font-medium text-gray-900 whitespace-nowrap">{row.name}</td>
                  {days.map((d) => {
                    const sh = row.days[d.toDateString()];
                    if (!sh) return <td key={d.toDateString()} className="py-2.5 px-2 text-center text-gray-300">—</td>;
                    const pending = sh.status === 'REQUESTED' || sh.status === 'PENDING';
                    const style = PATTERN_STYLE[sh.shiftPattern] || 'bg-gray-100 text-gray-800';
                    return (
                      <td key={d.toDateString()} className="py-2.5 px-2 text-center">
                        <span
                          className={`inline-block text-xs font-semibold px-2 py-1 rounded ${
                            pending ? 'bg-white text-amber-700 border border-dashed border-amber-400' : style
                          }`}
                        >
                          {sh.shiftPattern || 'Shift'}
                          {pending ? '?' : ''}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {staffRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center">
                    <p className="text-gray-500">No shifts assigned for this week.</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Create a rota period and assign shifts to see the week filled in here.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 px-6 py-4 border-b">Shift patterns</h3>
        <div className="px-6 py-4 flex flex-wrap gap-3">
          {patterns.map((p) => (
            <div key={p.id} className="border rounded-lg px-4 py-3 min-w-[170px]">
              <span className={`inline-block text-xs font-bold px-2 py-1 rounded ${PATTERN_STYLE[p.name] || 'bg-gray-100 text-gray-800'}`}>
                {p.name}
              </span>
              <p className="mt-2 text-sm font-semibold text-gray-900 tabular-nums">
                {p.startTime} – {p.endTime}
              </p>
              <p className="text-xs text-gray-500 tabular-nums">{Number(p.hours).toFixed(1)} hours</p>
              {p.description && <p className="text-xs text-gray-400 mt-1">{p.description}</p>}
            </div>
          ))}
          {patterns.length === 0 && <p className="text-gray-500 py-4">No shift patterns defined.</p>}
        </div>
      </div>
    </div>
  );
}
